import { useState, useEffect, useCallback } from "react";
import {
  MagicWand, SpinnerGap, DownloadSimple, PushPin, Sparkle,
  ArrowsClockwise, Palette, Plus, Trash, Star,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

export function downloadDataUrl(url, name = `visual-${Date.now()}.jpg`) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const MODELS = [
  { id: "gemini", label: "Gemini · Nano Banana" },
  { id: "openai", label: "OpenAI · GPT Image" },
];

export default function VisualGenerator({ defaultPrompt = "", sourceText = "", onAttach, onGenerated, compact = false }) {
  const { activeId } = useWorkspace();
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [provider, setProvider] = useState("gemini");
  const [count, setCount] = useState("1");
  const [styleId, setStyleId] = useState("none");
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [varyingId, setVaryingId] = useState(null);
  const [results, setResults] = useState([]);

  // manage looks
  const [manageOpen, setManageOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStyle, setNewStyle] = useState("");
  const [savingLook, setSavingLook] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get(`/brand-templates?workspace_id=${activeId}`);
      setTemplates(data);
    } catch { /* non-blocking */ }
  }, [activeId]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const selectedStyle = () => templates.find((t) => t.id === styleId)?.style || "";

  const suggest = async () => {
    const seed = (sourceText || prompt || "").trim();
    if (!seed) { toast.error("Type a topic or open a post first"); return; }
    if (!activeId) { toast.error("No workspace selected"); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post("/images/suggest-prompt", { workspace_id: activeId, text: seed });
      setPrompt(data.prompt);
      toast.success("Prompt drafted — tweak it or generate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not suggest a prompt");
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image you want"); return; }
    if (!activeId) { toast.error("No workspace selected"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/images/generate", {
        workspace_id: activeId, prompt, provider, count: Number(count), style: selectedStyle(),
      });
      setResults(data.images || []);
      toast.success(`Generated ${data.images.length} visual${data.images.length > 1 ? "s" : ""}`);
      onGenerated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally {
      setBusy(false);
    }
  };

  const vary = async (img) => {
    setVaryingId(img.id);
    try {
      const { data } = await api.post(`/images/${img.id}/variations`, { count: 2, style: selectedStyle() });
      setResults((r) => [...(data.images || []), ...r]);
      toast.success("Variations added");
      onGenerated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create variations");
    } finally {
      setVaryingId(null);
    }
  };

  const saveLook = async () => {
    if (!newName.trim() || !newStyle.trim()) { toast.error("Add a name and style words"); return; }
    setSavingLook(true);
    try {
      await api.post("/brand-templates", { workspace_id: activeId, name: newName, style: newStyle });
      setNewName(""); setNewStyle("");
      await loadTemplates();
      toast.success("Brand look saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save look");
    } finally {
      setSavingLook(false);
    }
  };

  const deleteLook = async (id) => {
    try {
      await api.delete(`/brand-templates/${id}`);
      if (styleId === id) setStyleId("none");
      await loadTemplates();
    } catch { toast.error("Delete failed"); }
  };

  const setDefaultLook = async (id) => {
    try {
      await api.post(`/brand-templates/${id}/default`);
      await loadTemplates();
      toast.success("Default look updated");
    } catch { toast.error("Could not set default"); }
  };

  const gridCls = compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  return (
    <div className="space-y-4" data-testid="visual-generator">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label htmlFor="vg-prompt">Describe your visual</Label>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-primary"
            data-testid="visual-suggest-btn" onClick={suggest} disabled={suggesting}>
            {suggesting ? <SpinnerGap size={14} className="animate-spin mr-1" /> : <Sparkle size={14} weight="fill" className="mr-1" />}
            Suggest prompt
          </Button>
        </div>
        <Textarea
          id="vg-prompt"
          data-testid="visual-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="min-h-[90px]"
          placeholder="e.g. A bold minimalist poster with a warm gradient background, editorial style — or tap ‘Suggest prompt’ to draft one from your post"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <Label className="mb-1.5 block">Model</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="visual-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`visual-model-${m.id}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 min-w-[160px]">
          <div className="flex items-center justify-between mb-1.5">
            <Label>Brand look</Label>
            <button type="button" data-testid="manage-looks-btn" onClick={() => setManageOpen(true)}
              className="text-xs text-primary hover:underline flex items-center gap-1">
              <Palette size={12} /> Manage
            </button>
          </div>
          <Select value={styleId} onValueChange={setStyleId}>
            <SelectTrigger data-testid="brand-look-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="brand-look-none">No brand look</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`brand-look-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full sm:w-24">
          <Label className="mb-1.5 block">Count</Label>
          <Select value={count} onValueChange={setCount}>
            <SelectTrigger data-testid="visual-count-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["1", "2", "3", "4"].map((n) => (
                <SelectItem key={n} value={n} data-testid={`visual-count-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button data-testid="visual-generate-btn" onClick={generate} disabled={busy}>
          {busy ? (
            <><SpinnerGap size={18} className="animate-spin mr-2" /> Generating…</>
          ) : (
            <><MagicWand size={18} weight="fill" className="mr-2" /> Generate</>
          )}
        </Button>
      </div>

      {busy && (
        <div className={`grid gap-4 ${gridCls}`}>
          {Array.from({ length: Number(count) }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg border border-border bg-secondary/40 animate-pulse" />
          ))}
        </div>
      )}

      {!busy && results.length > 0 && (
        <div className={`grid gap-4 ${gridCls}`} data-testid="visual-results">
          {results.map((img) => (
            <div key={img.id} data-testid={`visual-result-${img.id}`} className="group relative rounded-lg overflow-hidden border border-border">
              <img src={img.url || img.data_url} alt={img.prompt} className="w-full aspect-square object-cover" />
              {varyingId === img.id && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <SpinnerGap size={22} className="animate-spin text-white" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="icon" variant="secondary" data-testid={`visual-download-${img.id}`}
                  onClick={() => downloadDataUrl(img.url || img.data_url)} title="Download">
                  <DownloadSimple size={16} />
                </Button>
                <Button size="icon" variant="secondary" data-testid={`visual-vary-${img.id}`}
                  onClick={() => vary(img)} disabled={varyingId === img.id} title="Make variations">
                  <ArrowsClockwise size={16} />
                </Button>
                {onAttach && (
                  <Button size="icon" data-testid={`visual-attach-${img.id}`}
                    onClick={() => onAttach(img.url || img.data_url)} title="Attach to post">
                    <PushPin size={16} weight="fill" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* manage brand looks */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent data-testid="manage-looks-dialog">
          <DialogHeader>
            <DialogTitle>Brand looks</DialogTitle>
            <DialogDescription>
              Save a reusable style (colors, mood, art direction). Applied to every image you generate with it selected.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="looks-list">
            {templates.length === 0 && <p className="text-sm text-muted-foreground">No looks yet.</p>}
            {templates.map((t) => (
              <div key={t.id} data-testid={`look-item-${t.id}`} className="flex items-start gap-2 rounded-md border border-border p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    {t.name}
                    {t.is_default && <Badge variant="secondary" className="text-[10px]" data-testid={`look-default-badge-${t.id}`}>Default</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{t.style}</p>
                </div>
                <Button size="icon" variant="ghost" className={`shrink-0 ${t.is_default ? "text-primary" : ""}`}
                  data-testid={`set-default-look-${t.id}`} onClick={() => setDefaultLook(t.id)} title="Set as default">
                  <Star size={15} weight={t.is_default ? "fill" : "regular"} />
                </Button>
                <Button size="icon" variant="ghost" className="text-destructive shrink-0" data-testid={`delete-look-${t.id}`}
                  onClick={() => deleteLook(t.id)}><Trash size={15} /></Button>
              </div>
            ))}
          </div>

          <div className="space-y-2 border-t border-border pt-3">
            <Input data-testid="look-name-input" value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Look name — e.g. Warm Editorial" />
            <Textarea data-testid="look-style-input" value={newStyle} onChange={(e) => setNewStyle(e.target.value)}
              className="min-h-[70px]" placeholder="Style words — e.g. warm sunrise palette, muted neutrals, editorial photography, lots of negative space, soft shadows" />
            <Button className="w-full" data-testid="save-look-btn" onClick={saveLook} disabled={savingLook}>
              {savingLook ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Save look
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
